import {
  Button,
  column,
  Css,
  dateColumn,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  ModalBody,
  ModalFooter,
  ModalHeader,
  RadioGroupField,
  simpleDataRows,
  simpleHeader,
  SimpleHeaderAndData,
  TagGroup,
  useModal,
  withColumnGutters,
} from "@homebound/beam";
import { useMemo, useState } from "react";
import { dateCell, SearchBox } from "src/components";
import {
  ChangeEventWorkflow,
  CheckoutConfigsFilter,
  CheckoutConfigSideType,
  CheckoutConfigStatus,
} from "src/generated/graphqlTypes";
import { fail, queryResult } from "src/utils";
import { emptyCellDash } from "src/utils/constants";
import { ObjectState } from "src/utils/formState";
import { SelectConfigurationStep_StructureFragment } from "../1.SelectConfigurationStep.generated";
import { ApplyConfigurationFormInput } from "../applyConfigurationForm";
import {
  SelectConfiguration_CheckoutConfigFragment,
  SelectConfiguration_PotentialCheckoutConfigFragment,
  useSelectConfiguration_CheckoutConfigLazyQuery,
  useSelectConfiguration_CheckoutConfigQuery,
  useSelectConfiguration_PotentialConfigsQuery,
} from "./SelectConfigurationModal.generated";

type SelectConfigurationModalProps = {
  formState: ObjectState<ApplyConfigurationFormInput>;
  structure: SelectConfigurationStep_StructureFragment;
  initialSelectedConfigId?: string;
  onSelect: (config: SelectConfiguration_CheckoutConfigFragment) => void;
};

export function SelectConfigurationModal(props: SelectConfigurationModalProps) {
  const { formState, structure } = props;
  const isPartial = formState.workflow.value === ChangeEventWorkflow.ApplyPartialConfiguration;
  // The initial workflow can run on a structure with no config yet, so only the partial workflow requires one
  const configId = structure.readyPlanConfig?.checkoutConfig?.id;
  const query = useSelectConfiguration_CheckoutConfigQuery({
    variables: { configId: configId ?? "", structureId: structure.id },
    skip: !isPartial,
  });
  if (!isPartial) {
    return <SelectConfigurationModalPotentialConfigs {...props} />;
  } else {
    return queryResult(query, ({ checkoutConfig }) => (
      <SelectConfigurationModalPotentialConfigs {...props} currentConfig={checkoutConfig} />
    ));
  }
}

type SelectConfigurationModalPotentialConfigsProps = SelectConfigurationModalProps & {
  currentConfig?: SelectConfiguration_CheckoutConfigFragment;
};

function SelectConfigurationModalPotentialConfigs(props: SelectConfigurationModalPotentialConfigsProps) {
  const { structure, formState, onSelect, currentConfig, initialSelectedConfigId } = props;
  const [search, setSearch] = useState<string | undefined>(undefined);
  const query = useSelectConfiguration_PotentialConfigsQuery({
    variables: { filter: mapToFilter(structure, currentConfig, formState.workflow.value!), structureId: structure.id },
  });

  return queryResult(query, ({ checkoutConfigs }) => (
    <SelectConfigurationModalView
      structure={structure}
      // A partial configuration can't target the config it's changing, so the current one isn't an option
      checkoutConfigs={checkoutConfigs.entities.filter((cc) => cc.id !== currentConfig?.id)}
      initialSelectedConfigId={initialSelectedConfigId}
      onSelect={onSelect}
      search={search}
      setSearch={setSearch}
    />
  ));
}

type SelectConfigurationModalViewProps = Pick<
  SelectConfigurationModalProps,
  "initialSelectedConfigId" | "onSelect" | "structure"
> & {
  checkoutConfigs: SelectConfiguration_PotentialCheckoutConfigFragment[];
  search: string | undefined;
  setSearch: (search: string) => void;
};

function SelectConfigurationModalView(props: SelectConfigurationModalViewProps) {
  const { structure, checkoutConfigs, initialSelectedConfigId, onSelect, search, setSearch } = props;
  const { closeModal } = useModal();
  const [fetchSelectedConfig] = useSelectConfiguration_CheckoutConfigLazyQuery();

  // Defaults to "" rather than undefined so the radios stay controlled from the first render
  const [selectedConfigId, setSelectedConfigId] = useState(initialSelectedConfigId ?? "");

  const rows = useMemo(() => createRows(checkoutConfigs, structure.isAdu), [checkoutConfigs, structure]);
  const columns = useMemo(
    () => withColumnGutters(createColumns(selectedConfigId, setSelectedConfigId)),
    [selectedConfigId],
  );

  return (
    <>
      <ModalHeader>Select the Configuration to Replace</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap2.$}>
          <div css={Css.df.$}>
            {/* The wizard replaces its own url on mount, so a `?search=` write would clobber the draft's edit url. */}
            <SearchBox onSearch={setSearch} updateQueryString={false} />
          </div>
          <GridTable
            columns={columns}
            rows={rows}
            stickyHeader
            filter={search}
            fallbackMessage="There are no configurations available for this structure yet."
          />
        </div>
      </ModalBody>
      <ModalFooter>
        <Button label="Cancel" variant="quaternary" onClick={() => closeModal()} />
        <Button
          label="Select"
          data-testid="selectConfiguration"
          disabled={!selectedConfigId && "Select a configuration"}
          onClick={async () => {
            // Fetch the selected config data to pass to the onSelect callback so it can store the proposed RPOs on form-state
            const { data } = await fetchSelectedConfig({
              variables: { configId: selectedConfigId, structureId: structure.id },
            });
            const selectedConfig = data?.checkoutConfig;
            if (selectedConfig) onSelect(selectedConfig);
            closeModal();
          }}
        />
      </ModalFooter>
    </>
  );
}

type ConfigRow = {
  id: string;
  name: string;
  updatedAt: SelectConfiguration_PotentialCheckoutConfigFragment["updatedAt"];
  exteriorOptionNames: string[];
  interiorOptionNames: string[];
  version?: string;
  planName?: string;
  createdBy?: { id: string; name: string };
};
type Row = SimpleHeaderAndData<ConfigRow>;

function createColumns(selectedConfigId: string, onSelect: (configId: string) => void): GridColumn<Row>[] {
  return [
    column<Row>({
      header: emptyCell,
      // A single-option `RadioGroupField` per row, all reading the same `selectedConfigId`, so only the
      // selected row's radio is checked. Beam has no radio equivalent of `selectColumn`.
      // TODO: replace this once GridTable supports radio button selection natively.
      data: ({ id, name }) => (
        <RadioGroupField
          label={name ?? ""}
          labelStyle="hidden"
          value={selectedConfigId}
          onChange={onSelect}
          options={[{ label: "", value: id }]}
          data-testid="configRadio"
        />
      ),
      w: "1px",
      align: "center",
    }),
    column<Row>({
      header: "Configuration",
      data: ({ id, name }) => ({
        content: (
          <div css={Css.df.fdc.$}>
            <span css={Css.smSb.$}>{name}</span>
            <span css={Css.xs.$}>{id}</span>
          </div>
        ),
        value: `${name} ${id}`,
        sortValue: name,
      }),
    }),
    column<Row>({ header: "Created By", data: ({ createdBy }) => createdBy?.name ?? emptyCellDash }),
    column<Row>({ header: "Plan", data: ({ planName }) => planName ?? emptyCellDash }),
    column<Row>({ header: "Exterior", data: ({ exteriorOptionNames }) => tagCell(exteriorOptionNames) }),
    column<Row>({ header: "Interior", data: ({ interiorOptionNames }) => tagCell(interiorOptionNames) }),
    column<Row>({ header: "Version", data: ({ version }) => version ?? emptyCellDash, w: "70px" }),
    dateColumn<Row>({ header: "Last Updated", data: ({ updatedAt }) => dateCell(updatedAt), w: "120px" }),
  ];
}

function createRows(
  checkoutConfigs: SelectConfiguration_PotentialCheckoutConfigFragment[],
  isAdu: boolean,
): GridDataRow<Row>[] {
  const rows = checkoutConfigs
    // An ADU structure can only apply a checkout config that has an ADU side to it
    .filter((cc) => !isAdu || cc.config.side === CheckoutConfigSideType.Adu)
    .map(({ id, createdBy, updatedAt, config }) => ({
      id,
      name: config.name,
      createdBy: createdBy?.internalUser ?? undefined,
      planName: config.productOffering?.name,
      exteriorOptionNames: config.exteriorOptionNames,
      interiorOptionNames: config.interiorOptionNames,
      version: config.aggregateVersion?.displayVersion,
      updatedAt,
    }));

  return [simpleHeader, ...simpleDataRows(rows)];
}

function tagCell(names: string[]) {
  if (names.isEmpty) return emptyCellDash;
  // `value` keeps the cell searchable, since GridTable's `filter` only matches string/number cells
  return { content: <TagGroup tags={names.map((text) => ({ text }))} />, value: names.join(" ") };
}

function mapToFilter(
  structure: SelectConfigurationStep_StructureFragment,
  currentConfig: SelectConfiguration_CheckoutConfigFragment | undefined,
  workflow: ChangeEventWorkflow,
): CheckoutConfigsFilter {
  const filter = { structure: [structure.id], status: [CheckoutConfigStatus.Reserved] };
  if (workflow !== ChangeEventWorkflow.ApplyPartialConfiguration) return filter;
  // The partial configuration workflow only applies to the current plan + version
  const { config } = currentConfig ?? fail("Structure has no selected config");
  return { ...filter, productOffering: [config.productOffering!.id], aggregateVersion: [config.aggregateVersion!.id] };
}
